import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Edit, PlusCircle, Trash2, X } from "lucide-react";
import { apiFetch } from "../utils/apiClient";
import { showToast } from "../utils/toast";

const emptyDraft = { id: null, display_name: "", table_ids: [] };

const StewardManagement = ({ floorPlanEnabled = false, isDarkMode = true }) => {
  const [enabled, setEnabled] = useState(false);
  const [stewards, setStewards] = useState([]);
  const [tables, setTables] = useState([]);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const request = (url, options = {}) => apiFetch(url, {
    credentials: "include",
    cache: "no-store",
    ...options,
  });

  const load = useCallback(async () => {
    try {
      const settingsResponse = await request(`/api/stewards/settings?_ts=${Date.now()}`);
      const settings = await settingsResponse.json().catch(() => ({}));
      if (!settingsResponse.ok) throw new Error(settings.error || "Chargement impossible.");
      const active = Boolean(settings.enabled);
      setEnabled(active);
      if (!active) {
        setStewards([]);
        setTables([]);
        setDraft(null);
        return;
      }
      const [stewardsResponse, tablesResponse] = await Promise.all([
        request(`/api/stewards?_ts=${Date.now()}`),
        request(`/api/tables?_ts=${Date.now()}`),
      ]);
      const [stewardsPayload, tablesPayload] = await Promise.all([
        stewardsResponse.json().catch(() => ({})),
        tablesResponse.json().catch(() => []),
      ]);
      if (!stewardsResponse.ok || !tablesResponse.ok) {
        throw new Error(stewardsPayload.error || "Chargement impossible.");
      }
      setStewards(Array.isArray(stewardsPayload.stewards) ? stewardsPayload.stewards : []);
      setTables(Array.isArray(tablesPayload) ? tablesPayload : []);
    } catch (loadError) {
      setError(loadError.message);
    }
  }, []);

  useEffect(() => {
    void load();
    const refresh = () => void load();
    window.addEventListener("moo-steward-mode-updated", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener("moo-steward-mode-updated", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [load]);

  const zones = useMemo(() => {
    const groups = new Map();
    tables.forEach((table) => {
      const key = Number(table.zone_id) || 0;
      if (!groups.has(key)) {
        groups.set(key, {
          id: key,
          name: table.zone_name || table.zone || "Salle principale",
          tables: [],
        });
      }
      groups.get(key).tables.push(table);
    });
    return Array.from(groups.values());
  }, [tables]);

  if (!enabled) return null;

  const toggleTable = (tableId) => setDraft((current) => {
    const ids = current.table_ids.map(Number);
    return {
      ...current,
      table_ids: ids.includes(Number(tableId))
        ? ids.filter((id) => id !== Number(tableId))
        : [...ids, Number(tableId)],
    };
  });

  const toggleZone = (zone) => setDraft((current) => {
    const currentIds = current.table_ids.map(Number);
    const zoneIds = zone.tables.map((table) => Number(table.id));
    const allSelected = zoneIds.every((id) => currentIds.includes(id));
    return {
      ...current,
      table_ids: allSelected
        ? currentIds.filter((id) => !zoneIds.includes(id))
        : Array.from(new Set([...currentIds, ...zoneIds])),
    };
  });

  const persist = async (confirmReassign = false) => {
    const displayName = String(draft?.display_name || "").trim();
    if (!displayName) return;
    setSaving(true);
    setError("");
    try {
      const response = await request(
        draft.id ? `/api/stewards/${draft.id}` : "/api/stewards",
        {
          method: draft.id ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            display_name: displayName,
            table_ids: draft.table_ids,
            confirm_reassign: confirmReassign,
          }),
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (
        response.status === 409 &&
        payload.code === "STEWARD_TABLE_REASSIGN_CONFIRMATION_REQUIRED" &&
        !confirmReassign
      ) {
        const accepted = window.confirm(
          "Certaines tables ont déjà un steward. Les réaffecter ?",
        );
        if (accepted) {
          setSaving(false);
          await persist(true);
        }
        return;
      }
      if (!response.ok) throw new Error(payload.error || "Enregistrement impossible.");
      setDraft(null);
      await load();
      showToast("success", "Steward enregistré");
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  };

  const archive = async (steward) => {
    if (!window.confirm(`Supprimer ${steward.display_name} ?`)) return;
    const response = await request(`/api/stewards/${steward.id}`, {
      method: "DELETE",
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      setError(payload.error || "Suppression impossible.");
      return;
    }
    await load();
    showToast("success", "Steward supprimé");
  };

  const panel = isDarkMode
    ? "border-white/10 bg-white/5"
    : "border-slate-200 bg-white";
  return (
    <section className={`rounded-3xl border p-6 ${panel}`}>
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-2xl font-semibold">Stewards</h2>
        <button
          type="button"
          onClick={() => setDraft({ ...emptyDraft })}
          className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-2 font-semibold text-slate-950"
        >
          <PlusCircle size={18} />
          Ajouter un steward
        </button>
      </div>
      {error && <p className="mt-3 text-sm text-rose-400">{error}</p>}
      <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {stewards.map((steward) => (
          <article key={steward.id} className="rounded-2xl border border-white/10 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-lg font-semibold">{steward.display_name}</p>
                <p className="text-sm opacity-60">{steward.tables?.length || 0} table(s)</p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  aria-label={`Modifier ${steward.display_name}`}
                  onClick={() => setDraft({
                    id: steward.id,
                    display_name: steward.display_name,
                    table_ids: (steward.tables || []).map((table) => table.table_id),
                  })}
                  className="rounded-xl border border-white/10 p-2"
                >
                  <Edit size={16} />
                </button>
                <button
                  type="button"
                  aria-label={`Supprimer ${steward.display_name}`}
                  onClick={() => archive(steward)}
                  className="rounded-xl border border-rose-400/30 p-2 text-rose-300"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>

      {draft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl border border-white/10 bg-[#0f172a] p-6 text-white shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-2xl font-semibold">
                {draft.id ? "Modifier le steward" : "Ajouter un steward"}
              </h3>
              <button
                type="button"
                aria-label="Fermer"
                onClick={() => setDraft(null)}
                className="rounded-xl bg-white/10 p-2"
              >
                <X size={18} />
              </button>
            </div>
            <label className="mt-5 block">
              <span className="text-sm font-semibold">Identifiant</span>
              <input
                autoFocus
                value={draft.display_name}
                onChange={(event) => setDraft((current) => ({
                  ...current,
                  display_name: event.target.value,
                }))}
                className="mt-2 w-full rounded-2xl border border-white/15 bg-white/5 px-4 py-3"
              />
            </label>

            <div className="mt-6 space-y-4">
              {zones.map((zone) => (
                <div key={zone.id} className="rounded-2xl border border-white/10 p-4">
                  {floorPlanEnabled && (
                    <button
                      type="button"
                      onClick={() => toggleZone(zone)}
                      className="mb-3 font-semibold text-amber-300"
                    >
                      {zone.name}
                    </button>
                  )}
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                    {zone.tables.map((table) => {
                      const selected = draft.table_ids.map(Number).includes(Number(table.id));
                      return (
                        <button
                          key={table.id}
                          type="button"
                          aria-pressed={selected}
                          onClick={() => toggleTable(table.id)}
                          className={`rounded-xl border px-3 py-2 ${
                            selected
                              ? "border-amber-300 bg-amber-400/15 text-amber-200"
                              : "border-white/10"
                          }`}
                        >
                          {table.number}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {floorPlanEnabled && (
              <p className="mt-4 text-sm text-slate-400">
                Les tables ajoutées plus tard ne seront pas affectées automatiquement.
              </p>
            )}
            <button
              type="button"
              disabled={saving || !draft.display_name.trim()}
              onClick={() => persist(false)}
              className="mt-6 w-full rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 px-5 py-4 font-bold text-slate-950 disabled:opacity-50"
            >
              Enregistrer
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default StewardManagement;
